import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CreateParallelAi,
	ParallelAi,
	ParallelAiResponse,
	ParallelAiResponseSchema,
	ParallelAiSchema,
	ParallelAiTriggerResponse,
	ParallelAiTriggerResponseSchema,
} from "../../pages/ParallelAi/schema/parallelAi";

export const parallelAi = api.injectEndpoints({
	endpoints: (builder) => ({
		getParallelAiLists: builder.query<ParallelAiResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "parallel-ai",
				params: {
					...(params && { ...params }),
				},
				responseSchema: ParallelAiResponseSchema,
			}),
			providesTags: ["ParallelAi"],
		}),
		getParallelAiList: builder.query<ParallelAi, number>({
			query: (id: number) => ({
				url: `parallel-ai/${id}`,
				responseSchema: ParallelAiSchema,
			}),
			providesTags: ["ParallelAi"],
		}),
		createParallelAi: builder.mutation<Partial<ParallelAi>, CreateParallelAi>({
			query: (body) => ({
				url: `parallel-ai`,
				method: "POST",
				body,
				responseSchema: ParallelAiSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "ParallelAi"];
				}

				return ["ParallelAi"];
			},
		}),

		updateParallelAi: builder.mutation<
			Partial<ParallelAi>,
			Partial<ParallelAi> & { listId?: number }
		>({
			query: ({ id, ...body }) => ({
				url: `parallel-ai/${id}`,
				method: "PATCH",
				body,
				responseSchema: ParallelAiResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "ParallelAi"];
				}

				return ["ParallelAi"];
			},
		}),
		deleteParallelAi: builder.mutation<Partial<ParallelAi>, { id: number; listId?: number }>({
			query: ({ id, listId }) => ({
				url: listId ? `parallel-ai/${id}/list/${listId}` : `parallel-ai/${id}`,
				method: "DELETE",
				responseSchema: ParallelAiSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "ParallelAi"];
				}

				return ["ParallelAi"];
			},
		}),
		// Force an immediate, off-schedule monitor run (GLOBAL_ADMIN only on the API side).
		// Fire-and-forget: detections arrive via the normal poll/webhook path, so no tags change.
		triggerParallelAi: builder.mutation<ParallelAiTriggerResponse, number>({
			query: (id) => ({
				url: `parallel-ai/${id}/trigger`,
				method: "POST",
				responseSchema: ParallelAiTriggerResponseSchema,
			}),
		}),
	}),
});

export const {
	useGetParallelAiListsQuery,
	useLazyGetParallelAiListsQuery,
	useGetParallelAiListQuery,
	useUpdateParallelAiMutation,
	useDeleteParallelAiMutation,
	useCreateParallelAiMutation,
	useTriggerParallelAiMutation,
} = parallelAi;
export const accountApiReducer = parallelAi.reducer;
export const accountApiMiddleware = parallelAi.middleware;
